/**
 * 标准配合实体测量工具。
 *
 * 文件职责：
 * 1. 以可序列化 MateEntityRef 为输入计算 SOLIDWORKS 风格默认尺寸；
 * 2. 将渲染空间距离换算回 PropertyManager 中展示的线性尺寸；
 * 3. 避免 web-client 直接解析 BRep 或自行推导配合参数。
 */

import * as THREE from 'three';
import {
  MateDistanceMode,
  type MateDistanceEntityModes,
  type MateEntityRef,
} from '../../definition';
import {
  resolveStandardMateLinearDimensionScale,
} from './StandardMateDimension';
import {
  resolveAxisGeometry,
  resolvePlaneGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
  type PlaneGeometry,
  type PointGeometry,
} from '../geometry/StandardMateGeometryAccess';
import {
  calculateStandardMateDistanceValueFromCenterDistance,
  hasStandardMateRoundDistanceEntity,
  resolveStandardMateRoundDistanceModeOptions,
  resolveStandardMateDistanceEntityModes,
  resolveStandardMateRoundDistanceRadius,
} from './StandardMateRoundDistanceMode';

/** 标准测量容差。 */
const STANDARD_MATE_MEASUREMENT_EPSILON = 1e-9;

/**
 * 距离条件可用性。
 */
export interface StandardMateDistanceModeAvailability {
  /** 距离测量条件。 */
  readonly mode: MateDistanceMode;
  /** 当前两个实体是否可使用该条件。 */
  readonly enabled: boolean;
  /** 禁用原因。 */
  readonly disabledReason?: string;
}

/**
 * 距离配合逐实体圆形条件可用性。
 */
export interface StandardMateDistanceEntityModeOptions {
  /** 第一选择实体的圆形条件列表；实体不是圆形时为空。 */
  readonly firstEntityOptions: readonly StandardMateDistanceModeAvailability[];
  /** 第二选择实体的圆形条件列表；实体不是圆形时为空。 */
  readonly secondEntityOptions: readonly StandardMateDistanceModeAvailability[];
}

/**
 * 判断有限数字。
 *
 * @param value 待判断值。
 * @returns 是否为有限数字。
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断三维点元组。
 *
 * @param value 待判断值。
 * @returns 是否为三维点元组。
 */
function isPointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => isFiniteNumber(item));
}

/**
 * 从实体描述符读取有限曲面/曲线采样点。
 *
 * 设计说明：圆柱轴线、圆锥轴线是无限解析对象，直接用于平面距离会把有限曲面误判为 0。
 * SOLIDWORKS 在用户选择 Distance Mate 时会基于当前选择实体给出可感知的真实距离，
 * 因此这里优先消费 descriptor 中已有的代表点和采样点，不在加载阶段额外创建拓扑代理。
 *
 * @param entity 配合实体。
 * @returns 可用于默认距离测量的有限点集合。
 */
function resolveFiniteMeasurePoints(entity: MateEntityRef): readonly THREE.Vector3[] {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  const sampledPoints = Array.isArray(geometry.sampledPoints)
    ? geometry.sampledPoints.filter(isPointTuple)
    : [];
  if (sampledPoints.length > 0) {
    return sampledPoints.map((point) => new THREE.Vector3(point[0], point[1], point[2]));
  }

  const directPoint = [
    geometry.point,
    geometry.start,
    geometry.end,
    geometry.center,
    geometry.origin,
  ].find(isPointTuple);
  if (!directPoint) {
    return [];
  }

  return [new THREE.Vector3(directPoint[0], directPoint[1], directPoint[2])];
}

/**
 * 将点投影到轴线上。
 *
 * @param point 待投影点。
 * @param axis 目标轴线。
 * @returns 投影点。
 */
function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  return axis.origin.clone().addScaledVector(
    axis.direction,
    point.clone().sub(axis.origin).dot(axis.direction),
  );
}

/**
 * 计算点到轴线的最短距离。
 *
 * @param point 点。
 * @param axis 轴线。
 * @returns 最短距离。
 */
function calculatePointAxisDistance(point: THREE.Vector3, axis: AxisGeometry): number {
  return point.distanceTo(projectPointToAxis(point, axis));
}

/**
 * 计算两条轴线之间的最短距离。
 *
 * @param first 第一条轴线。
 * @param second 第二条轴线。
 * @returns 轴线之间的最短距离。
 */
function calculateAxisAxisDistance(first: AxisGeometry, second: AxisGeometry): number {
  const cross = first.direction.clone().cross(second.direction);
  if (cross.lengthSq() <= STANDARD_MATE_MEASUREMENT_EPSILON) {
    return calculatePointAxisDistance(second.origin, first);
  }
  return Math.abs(second.origin.clone().sub(first.origin).dot(cross.normalize()));
}

/**
 * 计算点到平面的最短距离。
 *
 * @param point 点。
 * @param plane 平面。
 * @returns 最短距离。
 */
function calculatePointPlaneDistance(point: THREE.Vector3, plane: PlaneGeometry): number {
  return Math.abs(point.clone().sub(plane.point).dot(plane.normal));
}

/**
 * 计算平面到轴线的最短距离。
 *
 * @param plane 平面。
 * @param axis 轴线。
 * @returns 最短距离。
 */
function calculatePlaneAxisDistance(plane: PlaneGeometry, axis: AxisGeometry): number {
  const directionDot = Math.abs(axis.direction.dot(plane.normal));
  if (directionDot > STANDARD_MATE_MEASUREMENT_EPSILON) {
    return 0;
  }
  return calculatePointPlaneDistance(axis.origin, plane);
}

/**
 * 解析轴类实体的距离参考点。
 *
 * 设计说明：圆柱面的轴线是无限解析对象，直接用任意轴原点会让与平面相交的轴线距离退化为 0。
 * SOLIDWORKS 的圆柱距离条件会基于当前选择的圆柱区域计算中心线距离，因此这里优先把曲面代表点
 * 投影到轴线上作为参考点。
 *
 * @param entity 实体引用。
 * @param axis 轴线几何。
 * @returns 轴线上用于距离测量的参考点。
 */
function resolveAxisDistanceReferencePoint(
  entity: MateEntityRef,
  axis: AxisGeometry,
): THREE.Vector3 {
  const representativePoint = resolveFiniteMeasurePoints(entity)[0] ?? null;
  return representativePoint ? projectPointToAxis(representativePoint, axis) : axis.origin.clone();
}

/**
 * 计算圆柱类中心线到平面的距离。
 *
 * @param plane 平面。
 * @param axis 轴线。
 * @param axisEntity 轴线所属实体。
 * @returns 中心线参考点到平面的距离。
 */
function calculatePlaneAxisCenterDistance(
  plane: PlaneGeometry,
  axis: AxisGeometry,
  axisEntity: MateEntityRef,
): number {
  if (typeof axis.radius === 'number' && axis.radius > STANDARD_MATE_MEASUREMENT_EPSILON) {
    return calculatePointPlaneDistance(resolveAxisDistanceReferencePoint(axisEntity, axis), plane);
  }
  return calculatePlaneAxisDistance(plane, axis);
}

/**
 * 计算平面到平面的默认距离。
 *
 * @param first 第一平面。
 * @param second 第二平面。
 * @returns 平面之间的距离。
 */
function calculatePlanePlaneDistance(first: PlaneGeometry, second: PlaneGeometry): number {
  const normalCross = first.normal.clone().cross(second.normal);
  if (normalCross.lengthSq() > STANDARD_MATE_MEASUREMENT_EPSILON) {
    return 0;
  }
  return calculatePointPlaneDistance(second.point, first);
}

/**
 * 计算有限点集合到平面的距离。
 *
 * @param points 有限点集合。
 * @param plane 平面。
 * @param reducer 聚合函数。
 * @returns 聚合后的距离；无点时返回 null。
 */
function calculateFinitePointsPlaneDistance(
  points: readonly THREE.Vector3[],
  plane: PlaneGeometry,
  reducer: (...values: number[]) => number,
): number | null {
  if (points.length === 0) {
    return null;
  }
  return reducer(...points.map((point) => calculatePointPlaneDistance(point, plane)));
}

/**
 * 计算有限点集合到轴线的距离。
 *
 * @param points 有限点集合。
 * @param axis 轴线。
 * @param reducer 聚合函数。
 * @returns 聚合后的距离；无点时返回 null。
 */
function calculateFinitePointsAxisDistance(
  points: readonly THREE.Vector3[],
  axis: AxisGeometry,
  reducer: (...values: number[]) => number,
): number | null {
  if (points.length === 0) {
    return null;
  }
  return reducer(...points.map((point) => calculatePointAxisDistance(point, axis)));
}

/**
 * 计算两个有限点集合之间的距离。
 *
 * @param first 第一组有限点。
 * @param second 第二组有限点。
 * @param reducer 聚合函数。
 * @returns 聚合后的距离；无点时返回 null。
 */
function calculateFinitePointsDistance(
  first: readonly THREE.Vector3[],
  second: readonly THREE.Vector3[],
  reducer: (...values: number[]) => number,
): number | null {
  if (first.length === 0 || second.length === 0) {
    return null;
  }
  const distances = first.flatMap((firstPoint) => (
    second.map((secondPoint) => firstPoint.distanceTo(secondPoint))
  ));
  return reducer(...distances);
}

/**
 * 读取实体的圆弧/圆柱半径。
 *
 * @param entity 实体引用。
 * @returns 半径；没有圆柱类距离条件语义时返回 0。
 */
/**
 * 判断两个实体是否具备圆形距离条件。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 是否具备可切换中心/最小/最大距离的圆形条件。
 */
function hasRoundDistanceCondition(
  first: MateEntityRef,
  second: MateEntityRef,
): boolean {
  return (
    hasStandardMateRoundDistanceEntity(first)
    || hasStandardMateRoundDistanceEntity(second)
  )
    && calculateCenterDistance(first, second) !== null;
}

/**
 * 读取实体可用于点距离测量的几何。
 *
 * @param entity 实体引用。
 * @returns 点几何。
 */
function resolveMeasurePoint(entity: MateEntityRef): PointGeometry | null {
  const point = resolvePointGeometry(entity);
  if (point) {
    return point;
  }
  const sphere = resolveSphereGeometry(entity);
  return sphere ? { point: sphere.center } : null;
}

/**
 * 计算两个实体中心线/中心点之间的距离。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 中心距离；无法测量时返回 null。
 */
function calculateCenterDistance(
  first: MateEntityRef,
  second: MateEntityRef,
): number | null {
  const firstPlane = resolvePlaneGeometry(first);
  const secondPlane = resolvePlaneGeometry(second);
  if (firstPlane && secondPlane) {
    return calculatePlanePlaneDistance(firstPlane, secondPlane);
  }

  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  if (firstAxis && secondAxis) {
    return calculateAxisAxisDistance(firstAxis, secondAxis);
  }

  if (firstPlane && secondAxis) {
    return calculatePlaneAxisCenterDistance(firstPlane, secondAxis, second);
  }
  if (firstAxis && secondPlane) {
    return calculatePlaneAxisCenterDistance(secondPlane, firstAxis, first);
  }

  const firstSphere = resolveSphereGeometry(first);
  const secondSphere = resolveSphereGeometry(second);
  if (firstSphere && secondSphere) {
    return firstSphere.center.distanceTo(secondSphere.center);
  }
  if (firstSphere && secondPlane) {
    return calculatePointPlaneDistance(firstSphere.center, secondPlane);
  }
  if (firstPlane && secondSphere) {
    return calculatePointPlaneDistance(secondSphere.center, firstPlane);
  }
  if (firstSphere && secondAxis) {
    return calculatePointAxisDistance(firstSphere.center, secondAxis);
  }
  if (firstAxis && secondSphere) {
    return calculatePointAxisDistance(secondSphere.center, firstAxis);
  }

  const firstPoint = resolveMeasurePoint(first);
  const secondPoint = resolveMeasurePoint(second);
  if (firstPoint && secondPoint) {
    return firstPoint.point.distanceTo(secondPoint.point);
  }
  if (firstPoint && secondPlane) {
    return calculatePointPlaneDistance(firstPoint.point, secondPlane);
  }
  if (firstPlane && secondPoint) {
    return calculatePointPlaneDistance(secondPoint.point, firstPlane);
  }
  if (firstPoint && secondAxis) {
    return calculatePointAxisDistance(firstPoint.point, secondAxis);
  }
  if (firstAxis && secondPoint) {
    return calculatePointAxisDistance(secondPoint.point, firstAxis);
  }

  return null;
}

/**
 * 计算两个实体的最小距离。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 最小距离；无法测量时返回 null。
 */
function calculateMinimumDistance(first: MateEntityRef, second: MateEntityRef): number | null {
  if (hasRoundDistanceCondition(first, second)) {
    const centerDistance = calculateCenterDistance(first, second);
    if (centerDistance !== null) {
      return Math.max(
        0,
        centerDistance
          - resolveStandardMateRoundDistanceRadius(first)
          - resolveStandardMateRoundDistanceRadius(second),
      );
    }
  }

  const firstPlane = resolvePlaneGeometry(first);
  const secondPlane = resolvePlaneGeometry(second);
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  const firstFinitePoints = resolveFiniteMeasurePoints(first);
  const secondFinitePoints = resolveFiniteMeasurePoints(second);

  return (
    (firstPlane ? calculateFinitePointsPlaneDistance(secondFinitePoints, firstPlane, Math.min) : null)
    ?? (secondPlane ? calculateFinitePointsPlaneDistance(firstFinitePoints, secondPlane, Math.min) : null)
    ?? (firstAxis ? calculateFinitePointsAxisDistance(secondFinitePoints, firstAxis, Math.min) : null)
    ?? (secondAxis ? calculateFinitePointsAxisDistance(firstFinitePoints, secondAxis, Math.min) : null)
    ?? calculateFinitePointsDistance(firstFinitePoints, secondFinitePoints, Math.min)
    ?? calculateCenterDistance(first, second)
  );
}

/**
 * 计算两个实体的最大距离。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 最大距离；无法测量时返回 null。
 */
function calculateMaximumDistance(first: MateEntityRef, second: MateEntityRef): number | null {
  if (hasRoundDistanceCondition(first, second)) {
    const centerDistance = calculateCenterDistance(first, second);
    if (centerDistance !== null) {
      return centerDistance
        + resolveStandardMateRoundDistanceRadius(first)
        + resolveStandardMateRoundDistanceRadius(second);
    }
  }

  const firstPlane = resolvePlaneGeometry(first);
  const secondPlane = resolvePlaneGeometry(second);
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  const firstFinitePoints = resolveFiniteMeasurePoints(first);
  const secondFinitePoints = resolveFiniteMeasurePoints(second);

  return (
    (firstPlane ? calculateFinitePointsPlaneDistance(secondFinitePoints, firstPlane, Math.max) : null)
    ?? (secondPlane ? calculateFinitePointsPlaneDistance(firstFinitePoints, secondPlane, Math.max) : null)
    ?? (firstAxis ? calculateFinitePointsAxisDistance(secondFinitePoints, firstAxis, Math.max) : null)
    ?? (secondAxis ? calculateFinitePointsAxisDistance(firstFinitePoints, secondAxis, Math.max) : null)
    ?? calculateFinitePointsDistance(firstFinitePoints, secondFinitePoints, Math.max)
    ?? calculateCenterDistance(first, second)
  );
}

/**
 * 解析默认距离条件。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 默认距离条件。
 */
export function resolveDefaultStandardMateDistanceMode(
  first: MateEntityRef,
  second: MateEntityRef,
): MateDistanceMode {
  return hasRoundDistanceCondition(first, second)
    ? MateDistanceMode.CenterToCenter
    : MateDistanceMode.Minimum;
}

/**
 * 解析当前实体组合可用的距离条件。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 距离条件可用性列表。
 */
export function resolveStandardMateDistanceModeOptions(
  first: MateEntityRef,
  second: MateEntityRef,
): readonly StandardMateDistanceModeAvailability[] {
  const hasRoundCondition = hasRoundDistanceCondition(first, second);
  if (!hasRoundCondition) {
    return [];
  }

  return [
    {
      mode: MateDistanceMode.CenterToCenter,
      enabled: calculateCenterDistance(first, second) !== null,
    },
    {
      mode: MateDistanceMode.Minimum,
      enabled: calculateMinimumDistance(first, second) !== null,
    },
    {
      mode: MateDistanceMode.Maximum,
      /**
       * SOLIDWORKS 的最大距离是圆形实体远侧之间的尺寸。只要几何能测量出远侧距离，
       * 就不应因为中心距小于半径和而禁用，否则平面-曲面相交或贴近时会误锁该选项。
       */
      enabled: calculateMaximumDistance(first, second) !== null,
    },
  ];
}

/**
 * 解析当前实体组合的逐实体圆形距离条件。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 第一/第二实体各自可用的圆形距离条件。
 */
export function resolveStandardMateDistanceEntityModeOptions(
  first: MateEntityRef,
  second: MateEntityRef,
): StandardMateDistanceEntityModeOptions {
  const centerDistance = calculateCenterDistance(first, second);
  return {
    firstEntityOptions: resolveStandardMateRoundDistanceModeOptions(first, centerDistance),
    secondEntityOptions: resolveStandardMateRoundDistanceModeOptions(second, centerDistance),
  };
}

/**
 * 判断 Distance Mate 当前实体组合是否允许显示同向/反向对齐。
 *
 * 设计说明：
 * 1. SOLIDWORKS 中两个平面做 Distance Mate 时仍可切换 Mate Alignment；
 * 2. 当实体组合具备圆形距离条件时，方向分支由“反转尺寸”和 Center/Minimum/Maximum 控制，对齐按钮不可用；
 * 3. 当前仅对平面-平面 Distance 暴露对齐，避免把圆柱、圆锥、圆弧等有轴向语义的距离配合错误混入对齐逻辑。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 是否允许同向/反向对齐。
 */
export function resolveStandardMateDistanceAlignmentEnabled(
  first: MateEntityRef,
  second: MateEntityRef,
): boolean {
  return Boolean(resolvePlaneGeometry(first) && resolvePlaneGeometry(second))
    && !hasRoundDistanceCondition(first, second);
}

/**
 * 计算两个 Mate 实体当前几何之间的渲染空间默认距离。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param distanceMode 距离测量条件。
 * @returns 渲染空间距离；无法测量时返回 null。
 */
export function measureStandardMateEntityDistanceInSceneUnit(
  first: MateEntityRef,
  second: MateEntityRef,
  distanceMode: MateDistanceMode = resolveDefaultStandardMateDistanceMode(first, second),
  distanceEntityModes?: MateDistanceEntityModes,
): number | null {
  if (distanceEntityModes) {
    const centerDistance = calculateCenterDistance(first, second);
    if (centerDistance === null) {
      return null;
    }
    return calculateStandardMateDistanceValueFromCenterDistance(
      centerDistance,
      first,
      second,
      resolveStandardMateDistanceEntityModes(first, second, distanceMode, distanceEntityModes),
    );
  }

  if (distanceMode === MateDistanceMode.CenterToCenter) {
    return calculateCenterDistance(first, second);
  }
  if (distanceMode === MateDistanceMode.Minimum) {
    return calculateMinimumDistance(first, second);
  }
  if (distanceMode === MateDistanceMode.Maximum) {
    return calculateMaximumDistance(first, second);
  }
  return null;
}

/**
 * 计算 PropertyManager 中 Distance Mate 应显示的默认线性尺寸。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param linearDimensionScale 线性尺寸到渲染空间的换算比例。
 * @param distanceMode 距离测量条件。
 * @returns 线性尺寸值；无法测量时返回 null。
 */
export function measureStandardMateDistanceDimensionValue(
  first: MateEntityRef,
  second: MateEntityRef,
  linearDimensionScale?: number,
  distanceMode?: MateDistanceMode,
  distanceEntityModes?: MateDistanceEntityModes,
): number | null {
  const sceneDistance = measureStandardMateEntityDistanceInSceneUnit(
    first,
    second,
    distanceMode,
    distanceEntityModes,
  );
  if (sceneDistance === null) {
    return null;
  }
  const scale = resolveStandardMateLinearDimensionScale(linearDimensionScale);
  return sceneDistance / scale;
}
